import { useState, useEffect } from 'react';
import { BRAND, FONTS } from '../styles/tokens';
import { Reveal, SectionLabel } from './ui/Reveal';
import { ASSESSMENTS } from '../data/assessments';
import { useIsMobile } from '../hooks/useIsMobile';
import { GUIDE_INTEREST } from '../data/content';
import { track } from '../services/analytics';
import { pagePath } from '../data/pages';
import type { Assessment, LeadPrefill } from '../types';

interface Props { onNav: (id: string) => void; onLead: (p: LeadPrefill) => void }

/**
 * Build the lead prefill for an assessment result.
 * Privacy rule: the Survivor and Self-Awareness assessments are personal — the site promises
 * their answers are never stored or shared — so only organizational (higher-is-better)
 * assessments pass an id/score/level through. Never the individual answers.
 */
export function leadPrefillFor(assessment: Assessment, pct: number, level: string): LeadPrefill {
  if (assessment.direction !== 'higher-is-better') return { interest: GUIDE_INTEREST };
  return { interest: GUIDE_INTEREST, assessmentId: assessment.id, assessmentScore: pct, assessmentLevel: level };
}

function scrollToAssessments() {
  const el = document.getElementById('assessments');
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export function Assessments({ onNav, onLead }: Props) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = ASSESSMENTS.find(a => a.id === selectedId) || null;

  const handleSelect = (id: string) => {
    setSelectedId(id);
    setTimeout(scrollToAssessments, 50);
  };
  const handleBack = () => {
    setSelectedId(null);
    setTimeout(scrollToAssessments, 50);
  };

  return (
    <section id="assessments" style={{ background: BRAND.white, padding: '0' }}>
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        {!selected ? <Picker onSelect={handleSelect} /> : <AssessmentQuiz assessment={selected} onNav={onNav} onLead={onLead} onBack={handleBack} />}
      </div>
    </section>
  );
}

function Picker({ onSelect }: { onSelect: (id: string) => void }) {
  const [hovered, setHovered] = useState<string | null>(null);
  const m = useIsMobile();
  return (
    <div style={{ padding: m ? '64px 20px' : '100px 24px' }}>
      <Reveal>
        <div style={{ textAlign: 'center', marginBottom: m ? 32 : 48 }}>
          <SectionLabel center>Free Assessments</SectionLabel>
          <h2 style={{ fontFamily: FONTS.heading, fontSize: m ? 28 : 38, fontWeight: 700, color: BRAND.navy, lineHeight: 1.18, marginBottom: 14 }}>Choose an assessment.</h2>
          <p style={{ fontFamily: FONTS.body, fontSize: m ? 14 : 16, color: BRAND.textMid, lineHeight: 1.7, maxWidth: 560, margin: '0 auto' }}>Each takes about 1–2 minutes. Your answers are private, never stored or shared.</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: m ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))', gap: m ? 12 : 16 }}>
          {ASSESSMENTS.map(a => (
            <div key={a.id} onClick={() => onSelect(a.id)}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(a.id); } }}
              role="button" tabIndex={0}
              onMouseEnter={() => setHovered(a.id)} onMouseLeave={() => setHovered(null)}
              style={{
                background: hovered === a.id ? BRAND.warmGlow : BRAND.cream,
                border: `1.5px solid ${hovered === a.id ? BRAND.warm : BRAND.border}`,
                borderRadius: 14, padding: m ? 22 : 28, cursor: 'pointer', transition: 'all 0.25s',
                transform: !m && hovered === a.id ? 'translateY(-3px)' : 'none',
                boxShadow: hovered === a.id ? '0 8px 24px rgba(0,0,0,0.06)' : 'none',
              }}>
              <div style={{ fontSize: m ? 28 : 32, marginBottom: 12 }}>{a.icon}</div>
              <h3 style={{ fontFamily: FONTS.heading, fontSize: m ? 18 : 20, fontWeight: 700, color: BRAND.navy, marginBottom: 4 }}>{a.title}</h3>
              <div style={{ fontFamily: FONTS.body, fontSize: 12, color: BRAND.warm, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>{a.subtitle}</div>
              <p style={{ fontFamily: FONTS.body, fontSize: 13, lineHeight: 1.6, color: BRAND.textLight }}>{a.description}</p>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, gap: 8 }}>
                <div style={{ fontFamily: FONTS.body, fontSize: 12, color: BRAND.warm, fontWeight: 600 }}>{a.questions.length} questions →</div>
                <a href={pagePath('assessment', a.slug)} onClick={e => e.stopPropagation()} style={{ fontFamily: FONTS.body, fontSize: 12, color: BRAND.textLight, textDecoration: 'none' }}>Open full page ↗</a>
              </div>
            </div>
          ))}
        </div>
      </Reveal>
    </div>
  );
}

/** The quiz engine. Used inline on the home page and on each /assessments/<slug>/ page. */
export function AssessmentQuiz({ assessment, onLead, onBack }: { assessment: Assessment; onNav?: (id: string) => void; onLead: (p: LeadPrefill) => void; onBack: () => void }) {
  const [step, setStep] = useState(-1);
  const [answers, setAnswers] = useState<number[]>([]);
  const [hoveredOpt, setHoveredOpt] = useState<string | null>(null);
  const m = useIsMobile();

  const [selectedAns, setSelectedAns] = useState<number | null>(null);

  // Scroll quiz into view on each step change
  useEffect(() => {
    if (step >= 0) setTimeout(scrollToAssessments, 50);
  }, [step]);

  // Completion event carries only the assessment id — never answers or level.
  useEffect(() => {
    if (step === 999) track('assessment_complete', { id: assessment.id });
  }, [step, assessment.id]);

  const handleAnswer = (val: number) => {
    setSelectedAns(val);
    const next = [...answers, val];
    setAnswers(next);
    setTimeout(() => {
      setSelectedAns(null);
      setStep(prev => prev < assessment.questions.length - 1 ? prev + 1 : 999);
    }, 350);
  };

  const score = answers.reduce((s, a) => s + (a === 0 ? 2 : a === 1 ? 1 : 0), 0);
  const maxScore = assessment.questions.length * 2;
  const scorePct = Math.round((score / maxScore) * 100);
  const pct = assessment.direction === 'higher-is-worse' ? 100 - scorePct : scorePct;
  const result = step === 999 ? assessment.getResult(score, maxScore) : null;

  const backBtn = <button onClick={onBack} style={{ fontFamily: FONTS.body, fontSize: 13, color: BRAND.textLight, background: 'none', border: 'none', cursor: 'pointer', marginBottom: m ? 16 : 24, padding: 0 }}>← All Assessments</button>;

  return (
    <div style={{ padding: m ? '64px 20px' : '100px 24px' }}>
      <Reveal>
        {/* Intro */}
        {step === -1 && (
          <div style={{ textAlign: 'center' }}>
            {backBtn}
            <div style={{ fontSize: 44, marginBottom: 16 }}>{assessment.icon}</div>
            <h2 style={{ fontFamily: FONTS.heading, fontSize: m ? 28 : 36, fontWeight: 700, color: BRAND.navy, lineHeight: 1.18, marginBottom: 12 }}>{assessment.title}</h2>
            <p style={{ fontFamily: FONTS.body, fontSize: m ? 14 : 16, color: BRAND.textMid, lineHeight: 1.7, maxWidth: 540, margin: '0 auto 12px' }}>{assessment.description}</p>
            <p style={{ fontFamily: FONTS.body, fontSize: 13, color: BRAND.textLight, marginBottom: 28, fontStyle: 'italic' }}>This is an educational tool, not a clinical or legal instrument.</p>
            {assessment.crisisResources && (
              <div style={{ background: BRAND.dangerBg, border: `1px solid ${BRAND.danger}30`, borderRadius: 10, padding: m ? 16 : 20, marginBottom: 28, textAlign: 'left', maxWidth: 500, margin: '0 auto 28px' }}>
                <div style={{ fontFamily: FONTS.body, fontSize: 13, fontWeight: 700, color: BRAND.danger, marginBottom: 8 }}>If you are in immediate danger, call 911.</div>
                {assessment.crisisResources.map((r, i) => (
                  <div key={i} style={{ fontFamily: FONTS.body, fontSize: 13, color: BRAND.text, marginBottom: 6 }}><strong>{r.name}</strong>: {r.contact}</div>
                ))}
              </div>
            )}
            <button onClick={() => setStep(0)} style={{ background: BRAND.navy, color: BRAND.white, border: 'none', padding: m ? '18px 40px' : '16px 40px', borderRadius: m ? 12 : 8, fontSize: 16, fontWeight: 700, fontFamily: FONTS.body, cursor: 'pointer', width: m ? '100%' : 'auto' }}>Begin Assessment</button>
          </div>
        )}

        {/* Questions */}
        {step >= 0 && step < assessment.questions.length && (
          <div>
            {backBtn}
            <div style={{ display: 'flex', gap: m ? 4 : 6, marginBottom: m ? 28 : 40 }}>
              {assessment.questions.map((_, i) => (
                <div key={i} style={{ flex: 1, height: 4, borderRadius: 2, background: i <= step ? BRAND.warm : BRAND.borderLight, transition: 'background 0.3s' }} />
              ))}
            </div>
            <div style={{ fontFamily: FONTS.body, fontSize: 12, color: BRAND.textLight, marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Question {step + 1} of {assessment.questions.length}
            </div>
            <h3 style={{ fontFamily: FONTS.heading, fontSize: m ? 20 : 24, fontWeight: 700, color: BRAND.navy, lineHeight: 1.3, marginBottom: m ? 24 : 32 }}>{assessment.questions[step].q}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {assessment.questions[step].options.map((opt, oi) => {
                const key = `${step}-${oi}`;
                return (
                  <button key={oi} onClick={() => handleAnswer(oi)} onMouseEnter={() => setHoveredOpt(key)} onMouseLeave={() => setHoveredOpt(null)}
                    disabled={selectedAns !== null}
                    aria-label={`Option: ${opt}`}
                    style={{
                      background: selectedAns === oi ? BRAND.warm : hoveredOpt === key ? BRAND.warmGlow : BRAND.cream,
                      border: `1.5px solid ${selectedAns === oi ? BRAND.warm : hoveredOpt === key ? BRAND.warm : BRAND.border}`,
                      borderRadius: 10, padding: m ? '16px 20px' : '18px 24px', textAlign: 'left',
                      fontFamily: FONTS.body, fontSize: m ? 14 : 15,
                      color: selectedAns === oi ? BRAND.navy : BRAND.text,
                      cursor: selectedAns !== null ? 'default' : 'pointer', transition: 'all 0.2s',
                      fontWeight: selectedAns === oi ? 700 : hoveredOpt === key ? 600 : 400,
                      opacity: selectedAns !== null && selectedAns !== oi ? 0.5 : 1,
                    }}>{selectedAns === oi ? `✓ ${opt}` : opt}</button>
                );
              })}
            </div>
          </div>
        )}

        {/* Results */}
        {step === 999 && result && (
          <div style={{ textAlign: 'center' }}>
            {backBtn}
            <div style={{ width: m ? 100 : 120, height: m ? 100 : 120, borderRadius: '50%', border: `6px solid ${result.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
              <span style={{ fontFamily: FONTS.heading, fontSize: m ? 28 : 36, fontWeight: 700, color: result.color }}>{pct}%</span>
            </div>
            <div style={{ fontFamily: FONTS.body, fontSize: 14, fontWeight: 700, color: result.color, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>{result.level}</div>
            <h2 style={{ fontFamily: FONTS.heading, fontSize: m ? 24 : 30, fontWeight: 700, color: BRAND.navy, marginBottom: 16 }}>{assessment.title} Results</h2>
            <p style={{ fontFamily: FONTS.body, fontSize: m ? 14 : 15, color: BRAND.textMid, lineHeight: 1.7, maxWidth: 500, margin: '0 auto 16px' }}>{result.summary}</p>
            <p style={{ fontFamily: FONTS.body, fontSize: 14, color: BRAND.textLight, lineHeight: 1.7, maxWidth: 500, margin: '0 auto 12px' }}><strong>Recommended next step:</strong> {result.nextSteps}</p>
            {assessment.crisisResources && (
              <div style={{ background: BRAND.cream, border: `1px solid ${BRAND.border}`, borderRadius: 10, padding: m ? 16 : 20, margin: '24px auto', textAlign: 'left', maxWidth: 500 }}>
                <div style={{ fontFamily: FONTS.body, fontSize: 12, fontWeight: 700, color: BRAND.textLight, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Support Resources</div>
                {assessment.crisisResources.map((r, i) => (
                  <div key={i} style={{ fontFamily: FONTS.body, fontSize: 13, color: BRAND.text, marginBottom: 8 }}><strong>{r.name}</strong><br /><span style={{ color: BRAND.warm }}>{r.contact}</span> — {r.description}</div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: m ? 'column' : 'row', gap: 12, justifyContent: 'center', marginTop: 28 }}>
              <button onClick={() => onLead(leadPrefillFor(assessment, pct, result.level))} style={{ background: BRAND.warm, color: BRAND.navy, border: 'none', padding: '14px 32px', borderRadius: m ? 12 : 8, fontSize: 14, fontWeight: 700, fontFamily: FONTS.body, cursor: 'pointer', width: m ? '100%' : 'auto' }}>Get the Free Guide</button>
              <button onClick={() => { setStep(-1); setAnswers([]); }} style={{ background: 'transparent', color: BRAND.navy, border: `1.5px solid ${BRAND.border}`, padding: '14px 32px', borderRadius: m ? 12 : 8, fontSize: 14, fontWeight: 500, fontFamily: FONTS.body, cursor: 'pointer' }}>Retake</button>
              <button onClick={onBack} style={{ background: 'transparent', color: BRAND.textMid, border: `1.5px solid ${BRAND.border}`, padding: '14px 32px', borderRadius: m ? 12 : 8, fontSize: 14, fontWeight: 500, fontFamily: FONTS.body, cursor: 'pointer' }}>Try Another</button>
            </div>
          </div>
        )}
      </Reveal>
    </div>
  );
}
